import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Download,
  UserPlus,
  Clock,
  Check,
  X,
  Ban,
  RotateCcw,
  ShieldPlus,
  ShieldMinus,
} from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

function convertToCSV(data) {
  if (!data.length) return "";
  const headers = Object.keys(data[0]).join(",");
  const rows = data.map((obj) =>
    Object.values(obj)
      .map((v) => `${String(v ?? "").replace(/"/g, '""')}`)
      .join(",")
  );
  return [headers, ...rows].join("\n");
}

function downloadCSV(csv, filename) {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
}

function isOwner(role) {
  return String(role || "").toLowerCase() === "owner";
}

function isAdmin(role) {
  return String(role || "").toLowerCase() === "admin";
}

export default function MembersTab({ groupId }) {
  const { t } = useTranslation();

  const [members, setMembers] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [joinRequests, setJoinRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  const [blockedMembers, setBlockedMembers] = useState([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);

  // ✅ Used to enable owner-only actions (like demote)
  const [myRole, setMyRole] = useState(null);

  const fetchMembers = useCallback(
    async (signal) => {
      try {
        setLoadingRequests(true);
        setLoadingBlocked(true);
2
        const [g, m, r, b, my] = await Promise.all([
          api.get(`/groups/${groupId}`),
          api.get(`/groups/${groupId}/members`),
          api.get(`/groups/${groupId}/join-requests`),
          api.get(`/groups/${groupId}/blocked-members`).catch(() => ({ data: [] })),
          // ✅ IMPORTANT: existing endpoint: /groups/:groupId/my-role
          api.get(`/groups/${groupId}/my-role`).catch(() => ({ data: { role: null } })),
        ]);

        if (!signal?.aborted) {
          setGroupName(g.data?.name || "group");
          setMembers(m.data || []);
          setJoinRequests(r.data || []);
          setBlockedMembers(b.data || []);
          setMyRole(my.data?.role || null);
        }
      } catch {
        if (!signal?.aborted) toast.error(t("groupAdminTabs.members.toastLoadError"));
      } finally {
        if (!signal?.aborted) {
          setLoadingRequests(false);
          setLoadingBlocked(false);
        }
      }
    },
    [groupId, t]
  );

  const handleExportMembers = async () => {
    try {
      const res = await api.get(`/groups/${groupId}/members/export`);
      const csv = convertToCSV(res.data || []);
      downloadCSV(csv, `${groupName}-members.csv`);
      toast.success(t("groupAdminTabs.members.toastExportSuccess"));
    } catch {
      toast.error(t("groupAdminTabs.members.toastExportError"));
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const id = setTimeout(() => fetchMembers(controller.signal), 0);
    return () => {
      controller.abort();
      clearTimeout(id);
    };
  }, [fetchMembers]);

  const activeMembersCount = members.filter((m) => m.membership_status === "active").length;

  const reloadData = async () => {
    await fetchMembers();
  };

  const handleApproveRequest = async (id) => {
    try {
      await api.post(`/groups/${groupId}/join-requests/${id}/approve`);
      toast.success(t("groupAdminTabs.members.toastApproveSuccess"));
      await reloadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || t("groupAdminTabs.members.toastApproveError"));
    }
  };

  // ✅ backend route is /reject
  const handleDenyRequest = async (id) => {
    try {
      await api.post(`/groups/${groupId}/join-requests/${id}/reject`);
      toast.success(t("groupAdminTabs.members.toastRejectSuccess"));
      await reloadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || t("groupAdminTabs.members.toastRejectError"));
    }
  };

  const handleBlockMember = async (member) => {
    const memberId = member?.user_id;
    if (!memberId) return;

    const name =
      `${member?.first_name || ""} ${member?.last_name || ""}`.trim() ||
      member?.email ||
      memberId;

    const ok = window.confirm(t("groupAdminTabs.members.blockConfirm", { name }));
    if (!ok) return;

    const reason = window.prompt(t("groupAdminTabs.members.blockReasonPrompt"));

    try {
      await api.post(`/groups/${groupId}/members/${memberId}/block`, {
        reason: reason || "",
      });
      toast.success(t("groupAdminTabs.members.blockSuccess"));
      await reloadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || t("groupAdminTabs.members.blockError"));
    }
  };

  const handleUnblockMember = async (blocked) => {
    const memberId = blocked?.user_id || blocked?.id;
    if (!memberId) return;

    const name =
      blocked?.user?.email ||
      blocked?.email ||
      `${blocked?.first_name || ""} ${blocked?.last_name || ""}`.trim() ||
      memberId;

    const readd = window.confirm(t("groupAdminTabs.members.unblockConfirmReadd", { name }));

    try {
      await api.post(`/groups/${groupId}/members/${memberId}/unblock`, { readd });
      toast.success(t("groupAdminTabs.members.unblockSuccess"));
      await reloadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || t("groupAdminTabs.members.unblockError"));
    }
  };

  const handlePromoteAdmin = async (member) => {
    const memberId = member?.user_id;
    if (!memberId) return;

    const name =
      `${member?.first_name || ""} ${member?.last_name || ""}`.trim() ||
      member?.email ||
      memberId;

    const ok = window.confirm(t("groupAdminTabs.members.promoteAdminConfirm", { name }));
    if (!ok) return;

    try {
      await api.post(`/groups/${groupId}/members/${memberId}/promote-admin`);
      toast.success(t("groupAdminTabs.members.promoteAdminSuccess"));
      await reloadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || t("groupAdminTabs.members.promoteAdminError"));
    }
  };

  const handleDemoteAdmin = async (member) => {
    const memberId = member?.user_id;
    if (!memberId) return;

    const name =
      `${member?.first_name || ""} ${member?.last_name || ""}`.trim() ||
      member?.email ||
      memberId;

    const ok = window.confirm(t("groupAdminTabs.members.demoteAdminConfirm", { name }));
    if (!ok) return;

    try {
      await api.post(`/groups/${groupId}/members/${memberId}/demote-admin`);
      toast.success(t("groupAdminTabs.members.demoteAdminSuccess"));
      await reloadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || t("groupAdminTabs.members.demoteAdminError"));
    }
  };

  const amOwner = String(myRole || "").toLowerCase() === "owner";

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{t("groupAdminTabs.members.title")}</h2>

        <div className="flex items-center gap-3 text-sm text-slate-500">
          <span>
            {t("groupAdminTabs.members.total")}: <strong>{members.length}</strong>
          </span>
          <span>
            {t("groupAdminTabs.members.active")}: <strong>{activeMembersCount}</strong>
          </span>

          <Button variant="outline" onClick={handleExportMembers}>
            <Download size={16} className="mr-2" />
            {t("groupAdminTabs.members.export")}
          </Button>
        </div>
      </div>

      {members.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            {t("groupAdminTabs.members.none")}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((m) => {
            const owner = isOwner(m.role);
            const admin = isAdmin(m.role);

            return (
              <Card key={m.user_id}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>
                      {m.first_name} {m.last_name}
                    </span>
                    <Badge variant="outline">{m.role}</Badge>
                  </CardTitle>
                  <CardDescription>{m.email}</CardDescription>
                </CardHeader>

                <CardContent className="text-sm text-slate-600">
                  {m.city && m.country ? (
                    <p>
                      {m.city}, {m.country}
                    </p>
                  ) : null}
                  {m.profession ? <p>{m.profession}</p> : null}

                  <div className="mt-2">
                    <Badge variant={m.membership_status === "active" ? "default" : "destructive"}>
                      {m.membership_status || t("groupAdminTabs.members.noMembership")}
                    </Badge>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      className="h-8 px-3 text-xs"
                      onClick={() => handlePromoteAdmin(m)}
                      disabled={owner || admin}
                      title={
                        owner
                          ? t("groupAdminTabs.members.tooltipOwnerImmutable")
                          : admin
                          ? t("groupAdminTabs.members.tooltipAlreadyAdmin")
                          : t("groupAdminTabs.members.tooltipPromoteAdmin")
                      }
                    >
                      <ShieldPlus size={14} className="mr-1" />
                      {t("groupAdminTabs.members.makeAdmin")}
                    </Button>

                    {/* Demote: enabled only for owner */}
                    {admin && !owner && (
                      <Button
                        variant="outline"
                        className="h-8 px-3 text-xs"
                        onClick={() => handleDemoteAdmin(m)}
                        disabled={!amOwner}
                        title={
                          amOwner
                            ? t("groupAdminTabs.members.tooltipDemoteAdmin")
                            : t("groupAdminTabs.members.tooltipOnlyOwnerDemote")
                        }
                      >
                        <ShieldMinus size={14} className="mr-1" />
                        {t("groupAdminTabs.members.demoteAdmin")}
                      </Button>
                    )}

                    <Button
                      variant="destructive"
                      className="h-8 px-3 text-xs"
                      onClick={() => handleBlockMember(m)}
                      disabled={owner}
                      title={owner ? t("groupAdminTabs.members.tooltipOwnerNoBlock") : t("groupAdminTabs.members.tooltipBlockMember")}
                    >
                      <Ban size={14} className="mr-1" />
                      {t("groupAdminTabs.members.block")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Blocked members */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">{t("groupAdminTabs.members.blockedTitle")}</h2>
          <span className="text-sm text-slate-500">
            {t("groupAdminTabs.members.blockedCount")}: <strong>{blockedMembers.length}</strong>
          </span>
        </div>

        {loadingBlocked ? (
          <Card>
            <CardContent className="py-8 text-center text-slate-500">
              {t("groupAdminTabs.members.loading")}
            </CardContent>
          </Card>
        ) : blockedMembers.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-slate-500">
              {t("groupAdminTabs.members.noBlocked")}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {blockedMembers.map((b) => (
              <Card key={b.user_id || b.id}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-base">
                    <span>{b.user?.email || b.email || b.user_id || b.id}</span>
                    <Badge variant="destructive">{t("groupAdminTabs.members.blockedBadge")}</Badge>
                  </CardTitle>
                  {(b.reason || b.blocked_at) && (
                    <CardDescription>
                      {b.reason ? b.reason : ""}
                      {b.reason && b.blocked_at ? " • " : ""}
                      {b.blocked_at ? format(new Date(b.blocked_at), "PPpp") : ""}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    className="h-8 px-3 text-xs w-full"
                    onClick={() => handleUnblockMember(b)}
                  >
                    <RotateCcw size={14} className="mr-1" />
                    {t("groupAdminTabs.members.unblock")}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Join Requests */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <UserPlus size={18} />
            {t("groupAdminTabs.members.requestsTitle")}
          </h2>
          <span className="text-sm text-slate-500">
            {t("groupAdminTabs.members.pending")}:{" "}
            <strong>{joinRequests.filter((r) => r.status === "pending").length}</strong>
          </span>
        </div>

        {loadingRequests ? (
          <Card>
            <CardContent className="py-8 text-center text-slate-500">
              {t("groupAdminTabs.members.loading")}
            </CardContent>
          </Card>
        ) : joinRequests.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-slate-500">
              {t("groupAdminTabs.members.noRequests")}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {joinRequests.map((r) => (
              <Card key={r.id}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-base">
                    <span>
                      {r.user
                        ? (`${r.user.first_name || ""} ${r.user.last_name || ""}`.trim() ||
                            r.user.email ||
                            t("groupAdminTabs.members.user"))
                        : t("groupAdminTabs.members.user")}
                    </span>
                    <Badge
                      variant={
                        r.status === "pending"
                          ? "outline"
                          : r.status === "approved"
                          ? "default"
                          : "destructive"
                      }
                    >
                      {r.status}
                    </Badge>
                  </CardTitle>
                  {r.user?.email && <CardDescription>{r.user.email}</CardDescription>}
                </CardHeader>

                <CardContent className="space-y-2 text-sm text-slate-600">
                  <p className="flex items-center text-xs text-slate-500">
                    <Clock size={12} className="mr-1" />
                    {r.created_at
                      ? format(new Date(r.created_at), "PPpp")
                      : t("groupAdminTabs.members.dateUnknown")}
                  </p>

                  {r.status === "pending" && (
                    <div className="flex gap-2 pt-2">
                      <Button
                        className="h-8 px-3 text-xs flex-1"
                        onClick={() => handleApproveRequest(r.id)}
                      >
                        <Check size={14} className="mr-1" />
                        {t("groupAdminTabs.members.approve")}
                      </Button>
                      <Button
                        variant="outline"
                        className="h-8 px-3 text-xs flex-1"
                        onClick={() => handleDenyRequest(r.id)}
                      >
                        <X size={14} className="mr-1" />
                        {t("groupAdminTabs.members.reject")}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
